import { type FC, type PropsWithChildren } from 'react'
import { CssBaseline, ThemeProvider as MuiThemeProvider, type PaletteMode, ScopedCssBaseline } from '@mui/material'
import { createTheme } from './theme.js'
import { ColorSchemeProvider } from './colorSchemeContext.js'
import { useColorScheme } from './use-color-scheme.js'
import { useDeviceDetect } from './device.js'
import type { DhlThemeOptions } from './types.js'
import { type Theme } from './augmented-theme.js'

export type ThemeOrFunc = Theme | ((theme: Theme) => Theme) | undefined

type Props = {
  theme?: ThemeOrFunc

  /**
   * Use generic css reset, can be usefull in shared applications like our locations widget
   * @default true
   */
  useCssBaseline?: boolean

  options?: DhlThemeOptions
}

type ThemeProviderProps = Props & {
  /**
   * Check for user preferences and set the theme accordingly
   * unless the user has interacted with theme settings for DHL before
   */
  detectTheme?: boolean
}

const determineTheme = (paletteMode: PaletteMode, theme: ThemeOrFunc, options: DhlThemeOptions): Theme => {
  const baseTheme = createTheme(paletteMode, options)

  if (typeof theme === 'function') {
    return theme(baseTheme)
  }
  return theme ?? baseTheme
}

const ThemeProviderComponent = ({ children, theme, useCssBaseline = true, options }: PropsWithChildren<Props>) => {
  const { activePaletteMode } = useColorScheme()
  const defaultDevice = useDeviceDetect()

  const determinedTheme = determineTheme(activePaletteMode, theme, {
    ...options,
    device: options?.device || defaultDevice,
  })

  if (!useCssBaseline) {
    return (
      <MuiThemeProvider theme={determinedTheme}>
        <ScopedCssBaseline>{children}</ScopedCssBaseline>
      </MuiThemeProvider>
    )
  }

  return (
    <MuiThemeProvider theme={determinedTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}

// TODO: this should be two types of ThemeProviders, one for the baseline and the other scoped...
// (now baseline code still gets packaged, even when not consuming)
export const ThemeProvider: FC<PropsWithChildren<ThemeProviderProps>> = (props) => {
  const { detectTheme, ...rest } = props

  return (
    <ColorSchemeProvider detectTheme={detectTheme}>
      <ThemeProviderComponent {...rest} />
    </ColorSchemeProvider>
  )
}
